import { useCallback, useEffect, useRef, useState } from "react";

import { experienceConfig } from "./config/experience.config";
import { usePrefersReducedMotion } from "./hooks/usePrefersReducedMotion";
import { Soundtrack, type SoundtrackHandle } from "./components/Soundtrack/Soundtrack";
import { EnvironmentFallback } from "./components/EnvironmentFallback/EnvironmentFallback";
import { RecoverableScene, type SceneStatus } from "./components/SceneRecovery/RecoverableScene";
import { LoadingNotice } from "./components/SceneRecovery/LoadingNotice";
import { birthdayProgress, hasPaintedFlower, hasProgress, type Chapter } from "./progress/birthdayProgress";
import { ProgressStorageNotice } from "./progress/ProgressStorageNotice";
import "./styles/progress-recovery.css";

type ExperiencePhase = "intro" | "cinematic" | "story";

const loadNightSky = () => import("./components/NightSky/NightSky");
const loadStoryExperience = () => import("./components/StoryExperience/StoryExperience");
const loadCelebration = () => import("./components/CelebrationCanvas/CelebrationCanvas");

export default function App() {
  const [savedChapter, setSavedChapter] = useState<Chapter | null>(() => {
    const saved = birthdayProgress.read(); return hasProgress(saved) ? saved!.chapter : null;
  });
  const [resumeChapter, setResumeChapter] = useState<Chapter>("arrival");
  const [confirmFreshStart, setConfirmFreshStart] = useState(false);
  const [phase, setPhase] = useState<ExperiencePhase>("intro");
  const [webglAvailable, setWebglAvailable] = useState(true);
  const [playSequence, setPlaySequence] = useState(0);
  const [candleExtinguished, setCandleExtinguished] = useState(false);
  const [finaleReady, setFinaleReady] = useState(false);
  const [finaleHasFlower, setFinaleHasFlower] = useState(false);
  const [fireworkRun, setFireworkRun] = useState(0);
  const [nightAttempt, setNightAttempt] = useState(0);
  const [nightStatus, setNightStatus] = useState<SceneStatus>("loading");
  const [nightSkipped, setNightSkipped] = useState(false);
  const [openingStarted, setOpeningStarted] = useState(false);
  const [endingOnly, setEndingOnly] = useState(false);
  const [celebrationBackend, setCelebrationBackend] = useState<
    "webgpu" | "webgl2" | "static"
  >("static");
  const transitionTimer = useRef<number | undefined>(undefined);
  const openingButtonRef = useRef<HTMLButtonElement>(null);
  const finaleTitleRef = useRef<HTMLHeadingElement>(null);
  const soundtrackRef = useRef<SoundtrackHandle>(null);
  const reducedMotion = usePrefersReducedMotion();

  const updateWebglAvailability = useCallback((available: boolean) => {
    setWebglAvailable(available);
  }, []);

  const completeIntro = useCallback(() => {
    setPhase((currentPhase) => (currentPhase === "cinematic" ? "story" : currentPhase));
  }, []);
  const startIntro = useCallback(() => setOpeningStarted(true), []);

  const continueWithoutNight = () => {
    if (phase === "intro") {
      void soundtrackRef.current?.start();
      setResumeChapter(savedChapter ?? "arrival");
    }
    setNightSkipped(true);
    setWebglAvailable(false);
    setPhase("story");
  };

  const retryOpening = () => {
    setOpeningStarted(false);
    setNightStatus("loading");
    setNightAttempt(value => value + 1);
  };

  const showFallbackEnding = () => {
    setFinaleHasFlower(hasPaintedFlower(birthdayProgress.read()?.flower));
    setEndingOnly(true);
    setCandleExtinguished(true);
    setCelebrationBackend("static");
    setFinaleReady(true);
  };

  const extinguishCandle = useCallback(() => {
    if (candleExtinguished) return;
    setFinaleReady(false);
    setCandleExtinguished(true);
  }, [candleExtinguished]);

  const completeCelebration = useCallback(() => {
    setFinaleHasFlower(hasPaintedFlower(birthdayProgress.read()?.flower));
    setFinaleReady(true);
  }, []);

  const revisitFlower = () => {
    birthdayProgress.patch({ chapter: "bloom" });
    setResumeChapter("bloom");
    setFinaleReady(false);
    setFinaleHasFlower(false);
    setCandleExtinguished(false);
    setEndingOnly(false);
    // Returning is not a replay: keep the mounted workshop and the audio/mute
    // choice. A later candle wish gets its own celebration, only on request.
    setFireworkRun(value => value + 1);
  };

  const syncCelebrationMusic = useCallback((elapsed: number) => {
    soundtrackRef.current?.syncCelebration(elapsed);
  }, []);

  const replayExperience = useCallback(() => {
    soundtrackRef.current?.reset();
    setFinaleReady(false);
    setFinaleHasFlower(false);
    setCandleExtinguished(false);
    setOpeningStarted(false);
    setEndingOnly(false);
    setNightSkipped(false);
    setCelebrationBackend("static");
    const saved = birthdayProgress.read();
    setSavedChapter(hasProgress(saved) ? saved!.chapter : null);
    setConfirmFreshStart(false);
    setPhase("intro");
    window.scrollTo({ top: 0, behavior: "auto" });
    window.requestAnimationFrame(() => openingButtonRef.current?.focus());
  }, []);

  const beginExperience = () => {
    if (phase !== "intro") return;
    void soundtrackRef.current?.start();
    if (reducedMotion) {
      setPhase("story");
      return;
    }

    setPhase("cinematic");
    setPlaySequence((sequence) => sequence + 1);
  };
  const resumeExperience = () => {
    if (phase !== "intro" || !savedChapter) return;
    setResumeChapter(savedChapter);
    void soundtrackRef.current?.start();
    setPhase("story");
  };
  const startFresh = () => {
    birthdayProgress.clear(); setSavedChapter(null); setConfirmFreshStart(false); setResumeChapter("arrival");
    beginExperience();
  };

  useEffect(() => {
    if (phase !== "cinematic" || webglAvailable) return;

    transitionTimer.current = window.setTimeout(() => setPhase("story"), 620);
    return () => {
      if (transitionTimer.current !== undefined) {
        window.clearTimeout(transitionTimer.current);
      }
    };
  }, [phase, webglAvailable]);

  useEffect(
    () => () => {
      if (transitionTimer.current !== undefined) {
        window.clearTimeout(transitionTimer.current);
      }
    },
    [],
  );

  useEffect(() => {
    // Prewarming is optional; a failed import must not become an unhandled rejection.
    void loadStoryExperience().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (finaleReady) finaleTitleRef.current?.focus({ preventScroll: true });
  }, [finaleReady]);

  return (
    <div
      className={`experience experience--${phase}${
        candleExtinguished ? " experience--celebrating" : ""
      }${finaleReady ? " experience--finale" : ""}${
        finaleReady && celebrationBackend !== "static"
          ? " experience--particle-finale"
          : ""
      }`}
    >
      {!nightSkipped && <RecoverableScene key={`night-${nightAttempt}`} load={loadNightSky} onStatus={setNightStatus}
        fallback={(failed, retry) => !candleExtinguished && <LoadingNotice scene="sky" failed={failed}
          onRetry={retry} onContinue={continueWithoutNight} continueLabel={phase === "story" ? "使用备用夜空" : "先去摘星"} />}>
        {({ NightSky }) => <NightSky
          reducedMotion={reducedMotion}
          playSequence={phase === "cinematic" ? playSequence : 0}
          storyActive={phase === "story"}
          candleExtinguished={candleExtinguished}
          celebrationOwnsBackdrop={candleExtinguished && celebrationBackend === "webgl2"}
          onAvailabilityChange={updateWebglAvailability}
          onIntroComplete={completeIntro}
          onIntroStart={startIntro}
        />}
      </RecoverableScene>}
      {phase === "cinematic" && nightStatus === "ready" && !openingStarted && !nightSkipped &&
        <LoadingNotice key={`opening-${nightAttempt}`} scene="opening" onRetry={retryOpening}
          onContinue={continueWithoutNight} continueLabel="先去摘星" />}
      <div className="atmosphere" aria-hidden="true" />
      {!webglAvailable && <EnvironmentFallback storyActive={phase === "story"}
        celebrating={candleExtinguished} reducedMotion={reducedMotion} />}
      <Soundtrack
        ref={soundtrackRef}
        phase={phase}
        candleExtinguished={candleExtinguished}
        finaleReady={finaleReady}
      />
      {phase === "story" && <ProgressStorageNotice />}

      {phase === "story" && !endingOnly && (
        <RecoverableScene load={loadCelebration}
          fallback={(failed, retry) => candleExtinguished && !finaleReady &&
            <LoadingNotice scene="celebration" failed={failed} onRetry={retry}
              onContinue={showFallbackEnding} continueLabel="跳至生日落款" />}>
          {({ CelebrationCanvas }) => <CelebrationCanvas
            active={candleExtinguished}
            runId={fireworkRun}
            reducedMotion={reducedMotion}
            webglAvailable={webglAvailable}
            onBackendChange={setCelebrationBackend}
            onComplete={completeCelebration}
            onPlaybackTime={syncCelebrationMusic}
          />}
        </RecoverableScene>
      )}

      {!webglAvailable && (
        <p className="rendering-note" role="status">
          夜空正以轻量方式呈现
        </p>
      )}

      <p className="experience-status" aria-live="polite">
        {phase === "cinematic"
          ? "星光正在夜色里展开"
          : finaleReady
            ? "生日祝福已经抵达"
            : candleExtinguished
              ? celebrationBackend === "static"
                ? "愿望已经收好"
                : "夜空正在绽放"
              : ""}
      </p>

      {phase !== "story" ? (
        <main className={`intro ${phase === "cinematic" ? "intro--leaving" : ""}`}>
          <div className="intro__content">
            <h1>{experienceConfig.intro.title}</h1>
            <p>{experienceConfig.intro.line}</p>
            <button
              ref={openingButtonRef}
              className="opening-button"
              type="button"
              onClick={confirmFreshStart ? startFresh : savedChapter ? resumeExperience : beginExperience}
              disabled={phase === "cinematic"}
            >
              <span>{confirmFreshStart ? "清除并重新开始" : savedChapter ? "接着刚才" : experienceConfig.intro.action}</span>
            </button>
            {savedChapter && <div className="intro-resume">
              <p>{confirmFreshStart ? "重新开始会清除已保存的摘星进度与花色。" : "星光与花色，都留在这里。进度仅保存在当前浏览器。"}</p>
              <button type="button" className="intro-resume__secondary" disabled={phase === "cinematic"}
                onClick={() => setConfirmFreshStart(value => !value)}>{confirmFreshStart ? "保留这次" : "重新开始"}</button>
            </div>}
          </div>
        </main>
      ) : !endingOnly && (
        <RecoverableScene load={loadStoryExperience}
          fallback={(failed, retry) => !candleExtinguished && (
            <main className="story story--loading" id="story">
              <section className="arrival" aria-labelledby="arrival-title-loading">
                <div className="arrival__copy">
                  <h1 id="arrival-title-loading">{experienceConfig.arrival.title}</h1>
                  <p>{experienceConfig.arrival.line}</p>
                  <LoadingNotice scene="story" failed={failed} onRetry={retry}
                    onContinue={showFallbackEnding} continueLabel="跳至生日落款" inline />
                </div>
              </section>
            </main>
          )}
        >
          {({ StoryExperience }) => <StoryExperience
            candleExtinguished={candleExtinguished}
            reducedMotion={reducedMotion}
            webglAvailable={webglAvailable}
            onExtinguish={extinguishCandle}
            resumeChapter={resumeChapter}
          />}
        </RecoverableScene>
      )}

      {phase === "story" && (
        <section
          className={`finale ${finaleReady ? "finale--ready" : ""}`}
          aria-labelledby="finale-title"
          aria-hidden={!finaleReady}
          inert={!finaleReady}
        >
          <div className="finale__copy">
            <h2 id="finale-title" ref={finaleTitleRef} tabIndex={-1}>
              {experienceConfig.finale.title}
            </h2>
            <p>{finaleHasFlower ? experienceConfig.finale.flowerLine : experienceConfig.finale.line}</p>
            {finaleHasFlower && <button className="finale__replay finale__flower" type="button" onClick={revisitFlower}>
              {experienceConfig.finale.flowerAction}
            </button>}
            <div className="finale__actions">
              <button className="finale__replay" type="button" onClick={replayExperience}>
                {experienceConfig.finale.replayAction}
              </button>
              {!reducedMotion && celebrationBackend !== "static" && <button className="finale__replay" type="button"
                onClick={() => { setFinaleReady(false); setFireworkRun(value => value + 1); }}>再放一场烟花</button>}
            </div>
            <p className="finale__music-credit">
              Music: <a href="https://www.scottbuckley.com.au/library/first-snow/" target="_blank" rel="noreferrer">First Snow — Scott Buckley</a>
              {" · "}<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">CC BY 4.0</a>
              {" · scene edit"}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
